import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { saveScore } from '../data/api';

interface DayStats {
  day: number;
  processed: number;
  quota: number;
  kpi: number;
  careerRisk: number;
  proceduralRisk: number;
}

interface Props {
  stats: DayStats;
  energy: number;
  onNextDay: () => void;
  onShareBonus: () => void;
}

type DifficultyRating = 'easy' | 'ok' | 'hard';

function getRating(kpi: number, careerRisk: number, proceduralRisk: number, lang = 'zh'): { label: string; cls: string } {
  const isEn = lang === 'en';
  const maxRisk = Math.max(careerRisk, proceduralRisk);
  if (kpi >= 70 && maxRisk < 40) return { label: isEn ? 'Excellent' : '优秀', cls: 'rating-excellent' };
  if (kpi >= 50 && maxRisk < 70) return { label: isEn ? 'Good'      : '良好', cls: 'rating-good' };
  if (kpi >= 30)                 return { label: isEn ? 'Pass'      : '及格', cls: 'rating-pass' };
  return                                { label: isEn ? 'Fail'      : '不合格', cls: 'rating-fail' };
}

const DayEndScreen: React.FC<Props> = ({ stats, energy, onNextDay, onShareBonus }) => {
  const { t, i18n } = useTranslation();
  const { label, cls } = getRating(stats.kpi, stats.careerRisk, stats.proceduralRisk, i18n.language);

  const [isAnonymous, setIsAnonymous] = useState(true);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [showNicknamePrompt, setShowNicknamePrompt] = useState(false);
  const [nicknameInput, setNicknameInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [nicknameSaved, setNicknameSaved] = useState(false);
  const difficultyKey = `borderjudge-difficulty-day${stats.day}`;
  const [difficultyRating, setDifficultyRating] = useState<DifficultyRating | null>(
    () => (localStorage.getItem(difficultyKey) as DifficultyRating | null)
  );
  const [shareCopied, setShareCopied] = useState(false);
  const [shareBonusGiven, setShareBonusGiven] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const userId = session.user.id;
      const anonymous = session.user.is_anonymous ?? false;
      setIsAnonymous(anonymous);

      if (!anonymous) {
        const meta = session.user.user_metadata;
        const displayName = meta?.full_name ?? meta?.name ?? meta?.user_name ?? session.user.email ?? null;
        setUserAvatar(meta?.avatar_url ?? meta?.picture ?? null);
        setUserName(displayName);

        if (displayName) {
          await supabase.from('profiles').upsert(
            { user_id: userId, nickname: displayName, avatar_url: meta?.avatar_url ?? meta?.picture ?? null },
            { onConflict: 'user_id' }
          );
        }
      }

      // Centralized score saving
      await saveScore(userId, stats.day, {
        kpi: stats.kpi,
        careerRisk: stats.careerRisk,
        proceduralRisk: stats.proceduralRisk,
      });

      if (anonymous) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('nickname')
          .eq('user_id', userId)
          .maybeSingle();

        if (!profile?.nickname) {
          setShowNicknamePrompt(true);
        }
      }
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveNickname = async () => {
    if (!nicknameInput.trim()) return;
    setIsSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      await supabase.from('profiles').upsert({
        user_id: session.user.id,
        nickname: nicknameInput.trim(),
      });
    }
    setNicknameSaved(true);
    setShowNicknamePrompt(false);
    setIsSaving(false);
  };

  const handleLinkProvider = async (provider: 'google' | 'github') => {
    await supabase.auth.linkIdentity({ provider });
  };

  const handleDifficultyRating = async (rating: DifficultyRating) => {
    setDifficultyRating(rating);
    localStorage.setItem(difficultyKey, rating);
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      await supabase.from('difficulty_feedback').insert({
        user_id: session.user.id,
        day: stats.day,
        rating,
      });
    }
  };

  const handleShare = async () => {
    const text = t('dayEnd.shareText', {
      day: stats.day,
      kpi: stats.kpi,
      careerRisk: stats.careerRisk,
      proceduralRisk: stats.proceduralRisk,
      rating: label,
    });
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2500);
    if (!shareBonusGiven) {
      setShareBonusGiven(true);
      onShareBonus();
    }
  };

  return (
    <div className="day-end-overlay">
      <div className="day-end-modal">
        <div className="day-end-title">
          {t('dayEnd.title', { day: stats.day })}
        </div>

        <div className="day-end-rating">
          <span className={`day-end-rating-badge ${cls}`}>{label}</span>
        </div>

        <div className="day-end-stats">
          <div className="day-end-stat">
            <div className="day-end-stat-value correct">{stats.kpi}</div>
            <div className="day-end-stat-label">KPI</div>
          </div>
          <div className="day-end-stat">
            <div className={`day-end-stat-value ${stats.careerRisk >= 70 ? 'wrong' : 'correct'}`}>{stats.careerRisk}</div>
            <div className="day-end-stat-label">{t('topbar.careerRisk')}</div>
          </div>
          <div className="day-end-stat">
            <div className={`day-end-stat-value ${stats.proceduralRisk >= 70 ? 'wrong' : 'correct'}`}>{stats.proceduralRisk}</div>
            <div className="day-end-stat-label">{t('topbar.proceduralRisk')}</div>
          </div>
        </div>

        <div className="day-end-difficulty">
          <div className="day-end-difficulty-label">{t('dayEnd.difficulty.prompt')}</div>
          <div className="day-end-difficulty-btns">
            {(['easy', 'ok', 'hard'] as DifficultyRating[]).map(r => (
              <button
                key={r}
                className={`day-end-difficulty-btn${difficultyRating === r ? ' selected' : ''}`}
                onClick={() => handleDifficultyRating(r)}
                disabled={difficultyRating !== null}
              >
                {t(`dayEnd.difficulty.${r}`)}
              </button>
            ))}
          </div>
        </div>

        {!isAnonymous && (
          <div className="day-end-user-info">
            {userAvatar && (
              <img className="day-end-user-avatar" src={userAvatar} alt="avatar" referrerPolicy="no-referrer" />
            )}
            <span className="day-end-user-name">{userName}</span>
          </div>
        )}

        {isAnonymous && showNicknamePrompt && (
          <div className="day-end-nickname-prompt">
            <div className="day-end-nickname-title">{t('dayEnd.nickname.title')}</div>
            <div className="day-end-nickname-row">
              <input
                className="day-end-nickname-input"
                type="text"
                maxLength={20}
                placeholder={t('dayEnd.nickname.placeholder')}
                value={nicknameInput}
                onChange={e => setNicknameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveNickname()}
              />
              <button
                className="day-end-nickname-save"
                onClick={handleSaveNickname}
                disabled={isSaving || !nicknameInput.trim()}
              >
                {isSaving ? '…' : t('dayEnd.nickname.save')}
              </button>
            </div>
            <div className="day-end-nickname-divider">{t('dayEnd.nickname.orLink')}</div>
            <div className="day-end-oauth-row">
              <button className="day-end-oauth-btn google" onClick={() => handleLinkProvider('google')}>
                Google
              </button>
              <button className="day-end-oauth-btn github" onClick={() => handleLinkProvider('github')}>
                GitHub
              </button>
            </div>
            <button className="day-end-skip" onClick={() => setShowNicknamePrompt(false)}>
              {t('dayEnd.nickname.skip')}
            </button>
          </div>
        )}

        {isAnonymous && nicknameSaved && (
          <div className="day-end-nickname-saved">{t('dayEnd.nickname.saved')}</div>
        )}

        <div className="day-end-energy">
          <span className="day-end-energy-label">{t('energy.label')}</span>
          {Array.from({ length: 5 }, (_, i) => (
            <span key={i} className={`energy-pip${i < energy ? ' full' : ''}`}>●</span>
          ))}
          {shareBonusGiven && (
            <span className="day-end-energy-bonus">{t('energy.shareBonus')}</span>
          )}
        </div>

        {energy > 0 ? (
          <button className="day-end-btn" onClick={onNextDay}>
            {t('dayEnd.nextDay', { day: stats.day + 1 })}
          </button>
        ) : (
          <div className="day-end-energy-empty">
            <div className="day-end-btn day-end-btn-disabled">{t('energy.empty')}</div>
            <div className="day-end-energy-hint">{t('energy.recoveryHint')}</div>
          </div>
        )}

        <div className="day-end-bottom-row">
          <button className="day-end-share-btn" onClick={handleShare} disabled={shareCopied}>
            {shareCopied ? `✅ ${t('dayEnd.shareCopied')}` : `📤 ${t('dayEnd.share')}`}
          </button>
          <button className="day-end-leaderboard-link" onClick={() => window.location.search = '?leaderboard'}>
            🏆 {t('leaderboard.title')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DayEndScreen;
